/*
* Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
*/
import React, {useCallback, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {PauseIcon} from '../../icons/PauseIcon';
import {PlayIcon} from '../../icons/PlayIcon';
import {dlsColor} from '../../theme/theme';
import {DLSTheme} from '../../types/types';
import {Seperator} from '../breadcrumbs/Seperator';
import Tooltip from '../tooltip/Tooltip';
import {IconButton} from './IconButton';
import LabelIconButton from './LabelIconButton';

interface PlayPauseButtonProps {
  onTogglePlayState: Function;
  isPlaying: boolean;
  showLabel: boolean;
  label: string;
  playTooltipText: string;
  pauseTooltipText: string;
  iconSize: number
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  seperatorStatusEvent : {
    color   : dlsColor('grey3'),
    padding : 10
  }
}));

/**
 * PlayPauseButton Component
 * 
 * A reusable UI component that toggles between play and pause states for asset updates.
 * Displays a Play or Pause icon depending on the current state, with optional label and tooltip.
 * 
 * Props:
 * - onTogglePlayState (Function): Callback invoked when toggled, receives the new playback state (boolean).
 * - isPlaying (boolean): Current playback state; determines which icon and tooltip are shown.
 * - showLabel (boolean): Whether to display a text label next to the button.
 * - label (string): The label text shown when `showLabel` is true.
 * - playTooltipText (string): Tooltip text shown when the button is in the "paused" state.
 * - pauseTooltipText (string): Tooltip text shown when the button is in the "playing" state.
 * - iconSize (number): Size of the Icon
 * 
 * ```typescript
 * import {PlayPauseButton} from './PlayPauseButton';
 *
 * export default function Example() {
 *   return (
 *     <PlayPauseButton>
 *       ...
 *     </PlayPauseButton>
 *   );
 * }
 * ```
 */
export const PlayPauseButton = ({
  label,
  iconSize,
  isPlaying,
  showLabel,
  playTooltipText,
  pauseTooltipText,
  onTogglePlayState
}: PlayPauseButtonProps) => {
  const classes                       = useStyles();
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const ButtonIcon  = !isPlaying ? PlayIcon : PauseIcon;
  const tooltipText = !isPlaying
    ? playTooltipText
    : pauseTooltipText;

  const setPlayback = useCallback(() => {
    onTogglePlayState?.(!isPlaying);
  }, [isPlaying]);

  const setToolTip = useCallback(({type}) => {
    setTooltipOpen(!(type === 'click' || type === 'mouseleave'));
  }, []);
 
  return (
    <>
      <LabelIconButton label={label} displayLabel={showLabel}>
        <Tooltip arrow
          title={tooltipText}
          open={tooltipOpen}>
          <IconButton icon={<ButtonIcon size={iconSize} />}
            onMouseEnter={setToolTip}
            onMouseLeave={setToolTip}
            onClick={setPlayback} />
        </Tooltip>
      </LabelIconButton>
      <Seperator seperator='|' className={classes.seperatorStatusEvent} />
    </>
  );
};

export default PlayPauseButton;
